import { format } from "date-fns";
import type { Locale } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { Namespace, TFn } from "@racoon/i18n";
import { fromTimestamp, sortJobsByStep } from "@racoon/lib";
import { fullName } from "~/shared/helpers";
import { jobTypeLabel } from "../utils/jobTypeLabel";
import { plannedContainerCount } from "~/shared/lib/job-containers";

/** One line of the flat table: a single job the driver did on a given shift/route. */
export type RouteSheetPdfRow = {
  index: number;
  routeLabel: string;
  customerName: string;
  customerAddress: string;
  jobId: string;
  address: string;
  containerType: string;
  plannedCount: string;
  timeWindow: string;
  note: string;
};

export type RouteSheetPdfData = {
  title: string;
  brand: string;
  plateNumber: string;
  driver: string;
  carrier: string;
  wasteTypes: string;
  rows: RouteSheetPdfRow[];
  totalPlannedContainers: number;
};

const DASH = "-";

// A route sheet is the driver's plan, so it lists only jobs still waiting to
// be done; completed, cancelled and in-progress ones are left out, and so are
// pseudo-route jobs, which record trips that already happened.
export const isRouteSheetJob = (job: ConnectProto.Job): boolean =>
  job.status === ConnectProto.JobStatus.ASSIGNED && !job.isPseudo;

const formatTime = (timestamp?: { seconds?: string | number | bigint; nanos?: number }): string => {
  const date = fromTimestamp(timestamp);
  return date ? format(date, "HH:mm") : DASH;
};

const buildTimeWindow = (job: ConnectProto.Job): string =>
  `${formatTime(job.planStartAt)}\n${formatTime(job.planEndAt)}`;

const buildPlannedCount = (job: ConnectProto.Job): string => {
  const count = plannedContainerCount(job);
  return count > 0 ? String(count) : DASH;
};

const buildContainerType = (job: ConnectProto.Job): string =>
  job.containerGroup?.containerType?.name ?? job.containerType?.name ?? DASH;

const buildJobRow = (job: ConnectProto.Job, routeLabel: string): Omit<RouteSheetPdfRow, "index"> => ({
  routeLabel,
  customerName: job.serviceReceiver?.name || DASH,
  customerAddress: job.serviceReceiver?.factAddress || job.serviceReceiver?.jurAddress || "",
  jobId: `Z${String(job.id)}`,
  address: job.pickupLocation?.address || job.pickupLocation?.name || DASH,
  containerType: buildContainerType(job),
  plannedCount: buildPlannedCount(job),
  timeWindow: buildTimeWindow(job),
  note: job.driverComment || DASH,
});

/**
 * Shape a truck's whole day into a single flat table: every job the driver
 * did, one row each, in shift -> route -> job order.
 */
export const buildRouteSheetPdfData = (params: {
  shifts: ConnectProto.DriverOnShift[];
  routesByShiftId: Map<bigint, ConnectProto.Route[]>;
  carrierName?: string;
  t: TFn;
  dateFnsLocale: Locale;
  date?: Date;
}): RouteSheetPdfData => {
  const { shifts, routesByShiftId, carrierName, t, dateFnsLocale, date } = params;

  const allRoutes = shifts.flatMap((shift) => routesByShiftId.get(shift.id) ?? []);
  const allJobs = allRoutes.flatMap((route) => (route.jobs ?? []).filter(isRouteSheetJob));
  const truck = shifts.find((shift) => shift.truck)?.truck;

  const drivers = Array.from(
    new Set(shifts.map((shift) => fullName(shift.driver)).filter((name) => name.length > 0)),
  );
  const wasteTypes = Array.from(new Set(allJobs.map((job) => jobTypeLabel(job.type, t))));

  const rows: RouteSheetPdfRow[] = [];
  for (const shift of shifts) {
    // Ascending by id, same order the on-screen route cascade uses — the
    // list API can return them in an arbitrary (even descending) order.
    // Compared as bigint, not Number(), since ids can exceed 2^53.
    const routes = [...(routesByShiftId.get(shift.id) ?? [])].sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    );

    for (const route of routes) {
      const routeLabel = route.driverOnShift?.truck?.plateNumber ?? truck?.plateNumber ?? DASH;
      // The API doesn't guarantee job order; stepIdx is the actual visit order.
      for (const job of sortJobsByStep((route.jobs ?? []).filter(isRouteSheetJob))) {
        rows.push({ index: 0, ...buildJobRow(job, routeLabel) });
      }
    }
  }
  rows.forEach((row, i) => {
    row.index = i + 1;
  });

  const totalPlannedContainers = allJobs.reduce(
    (sum, job) => sum + plannedContainerCount(job),
    0,
  );

  return {
    // The waybill number is left blank on purpose: it's assigned by hand on
    // the printed sheet until there's a platform-tracked waybill id to use.
    title: t(Namespace.Routes, "routeSheetPdf.title", {
      date: date ? format(date, "dd.MM.yyyy", { locale: dateFnsLocale }) : DASH,
    }),
    brand:
      [truck?.truckType?.manufacturer, truck?.truckType?.model ?? truck?.model].filter(Boolean).join(" ") ||
      DASH,
    plateNumber: truck?.plateNumber ?? DASH,
    driver: drivers.length > 0 ? drivers.join(", ") : DASH,
    // The shifts endpoint doesn't preload the truck's owner organization; its
    // trucks are all owned by the active company, so that name is the fallback.
    carrier: truck?.ownerOrganization?.name || carrierName || DASH,
    wasteTypes: wasteTypes.length > 0 ? wasteTypes.join(", ") : DASH,
    rows,
    totalPlannedContainers,
  };
};
